<template>
  <section v-if="state?.enabled" class="free-pick" :class="{ 'free-pick--compact': compact }"
    :aria-labelledby="authStore.isAuthenticated ? titleId : undefined"
    :aria-label="authStore.isAuthenticated ? undefined : 'Free library account access'">
    <template v-if="!authStore.isAuthenticated">
      <p class="free-pick__guest">
        <button type="button" class="free-pick__text-button" @click="showAccount">Log in / Create account</button>
        to choose your free library.
      </p>
    </template>
    <template v-else>
    <h2 :id="titleId" ref="heading" class="free-pick__title" tabindex="-1">Want more for FREE?</h2>
    <p v-if="state.unavailableReason" class="free-pick__error" role="alert">{{ state.unavailableReason }}</p>
    <template v-else>
      <div class="free-pick__progress" aria-hidden="true">
        <i :style="{ width: `${progress}%` }" />
        <span v-for="(threshold, index) in state.thresholds" :key="index"
          class="free-pick__marker" :class="{ 'is-unlocked': index < state.highestUnlockedTier }"
          :style="{ left: `${(index + 1) * 25}%` }">{{ money(threshold) }}</span>
      </div>
      <p class="free-pick__status" role="status">
        <strong v-if="state.highestUnlockedTier">{{ tierNames[state.highestUnlockedTier - 1] }} unlocked.</strong>
        {{ state.availableTier ? 'Choose one free library from your highest available tier.' : state.highestUnlockedTier ? '' : 'Unlock one free library with your order.' }}
        <span v-if="state.amountToNextTier">
          Add {{ money(state.amountToNextTier) }} more to unlock {{ tierNames[state.highestUnlockedTier] }}.
        </span>
        <span v-else-if="state.highestUnlockedTier === 4">You have reached the top tier.</span>
      </p>
      <p class="free-pick__hint">One free library per qualifying order. If a tier has no eligible titles, choose from the next available lower tier. Rewards do not stack.</p>
      <p v-if="state.availableTier" class="free-pick__hint">Your free library includes {{ state.seatCount }} {{ state.seatCount === 1 ? 'seat' : 'seats' }}.</p>
      <p v-if="state.availableTier > 0 && state.availableTier < state.highestUnlockedTier" class="free-pick__note" role="status">
        Your higher unlocked tiers have no eligible titles. Choose one free library from {{ tierNames[state.availableTier - 1] }}.
      </p>
      <p v-else-if="authStore.isAuthenticated && state.highestUnlockedTier > 0 && !state.availableTier" class="free-pick__note" role="status">
        No eligible libraries are available in your unlocked tiers right now.
      </p>
      <p v-if="selectionNotice" class="free-pick__note" role="status">{{ selectionNotice }}</p>
      <p v-if="checkoutReminder && state.availableTier > 0 && !state.selectedProductId"
        class="free-pick__note" role="status">
        You've unlocked a Free Library! Choose your freebie now before completing checkout, as rewards cannot be claimed after purchase.
      </p>

      <ol class="free-pick__slots" aria-label="Free Pick tiers">
        <li v-if="state.availableTier" class="free-pick__slot is-active">
          <h3 class="free-pick__tier-name">{{ tierNames[state.availableTier - 1] }}</h3>
          <p class="free-pick__tier-value">{{ money(state.thresholds[state.availableTier - 1]) }} paid value</p>
          <template v-if="displayedProduct">
            <FreePickProductCard :product="displayedProduct" :badge="state.selectedProductId ? 'Selected' : 'Suggested'">
              <template v-if="state.selectedProductId">
                <div class="free-pick__links">
                  <button type="button" :disabled="busy" @click="openPicker">Change</button>
                  <button type="button" :disabled="busy" @click="clearSelection">Remove</button>
                </div>
              </template>
              <template v-else>
                <button type="button" class="free-pick__button" :disabled="busy" @click="select(displayedProduct.productId)">Add free</button>
                <button v-if="state.candidates.nodes.length > 1 || state.candidates.hasMore"
                  type="button" class="free-pick__button free-pick__button--dark" :disabled="busy" @click="openPicker">See all eligible titles</button>
              </template>
            </FreePickProductCard>
          </template>
          <template v-else>
            <p class="free-pick__hint">{{ state.selectedProductId ? 'Your free library is selected.' : 'No eligible libraries are available in this tier right now.' }}</p>
            <button v-if="state.selectedProductId" type="button" class="free-pick__text-button" :disabled="busy" @click="clearSelection">Remove selection</button>
            <button v-if="state.candidates.hasMore || state.selectedProductId"
              type="button" class="free-pick__text-button" :disabled="busy" @click="openPicker">
              {{ state.selectedProductId ? 'Change selection' : 'Browse more libraries' }}
            </button>
          </template>
        </li>
      </ol>
    </template>
    </template>
    <p v-if="errorMessage && !showPicker" class="free-pick__error" role="alert">{{ errorMessage }}</p>

    <Teleport to="body">
      <div v-if="showPicker" class="free-pick__overlay" @click.self="closePicker" @keydown.esc.stop="closePicker">
        <section ref="pickerDialog" class="free-pick__dialog" role="dialog" aria-modal="true"
          :aria-labelledby="pickerTitleId" tabindex="-1" @keydown="trapFocus">
          <header class="free-pick__dialog-header">
            <h3 :id="pickerTitleId">Choose a free library: {{ tierNames[state.availableTier - 1] }}</h3>
            <button ref="closeButton" type="button" class="free-pick__close" aria-label="Close library picker" @click="closePicker">&times;</button>
          </header>
          <p class="free-pick__hint">Choose one title. Changing your selection replaces your current free library.</p>
          <div class="free-pick__grid" :aria-busy="busy || isLoadingCandidates">
            <FreePickProductCard v-for="product in state.candidates.nodes" :key="product.productId" :product="product"
              :badge="product.productId === state.selectedProductId ? 'Selected' : undefined">
              <button type="button" class="free-pick__button" :disabled="busy || product.productId === state.selectedProductId"
                @click="select(product.productId)">{{ product.productId === state.selectedProductId ? 'Selected' : 'Add free' }}</button>
            </FreePickProductCard>
          </div>
          <p v-if="!state.candidates.nodes.length" class="free-pick__hint">
            {{ state.candidates.hasMore ? 'No eligible titles on this page. Load more to keep browsing.' : 'No eligible libraries are available in this tier right now.' }}
          </p>
          <button v-if="state.candidates.hasMore" type="button" class="free-pick__button free-pick__load"
            :disabled="busy || isLoadingCandidates" @click="loadMoreCandidates">
            {{ isLoadingCandidates ? 'Loading...' : 'Load more libraries' }}
          </button>
          <p v-if="errorMessage" class="free-pick__error" role="alert">{{ errorMessage }}</p>
        </section>
      </div>
      <login-overlay v-if="showLogin" @cancel-close="closeAccount" @login-successful="afterLogin" />
    </Teleport>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from '#imports';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import { useAuthStore } from '~/stores/auth';
import { useCartStore } from '~/stores/cart';
import LoginOverlay from '~/components/parts/login/LoginOverlay.vue';
import FreePickProductCard from './FreePickProductCard.vue';
import type { FreePickState } from '~/stores/types/cart';

defineProps<{ compact?: boolean; checkoutReminder?: boolean }>();

const authStore = useAuthStore();
const cartStore = useCartStore();
const tierNames = ['Essential', 'Core', 'Advanced', 'Premium'];
const id = useId();
const titleId = `free-pick-title-${id}`;
const pickerTitleId = `free-pick-picker-title-${id}`;
const heading = ref<HTMLElement | null>(null);
const pickerDialog = ref<HTMLElement | null>(null);
const closeButton = ref<HTMLButtonElement | null>(null);
const showPicker = ref(false);
const showLogin = ref(false);
const isLoadingCandidates = ref(false);
const isMutating = ref(false);
const errorMessage = ref('');
const selectionNotice = ref('');
let opener: HTMLElement | null = null;
let lockedDialog: HTMLElement | null = null;
const state = computed<FreePickState | undefined>(() => cartStore.getCurrentCart?.freePick);
const busy = computed(() => isMutating.value || cartStore.isLoading);
const displayedProduct = computed(() => state.value?.selectedProductId
  ? state.value.selectedProduct ?? state.value.candidates.nodes.find(product => product.productId === state.value?.selectedProductId)
  : state.value?.candidates.nodes[0]);
const progress = computed(() => {
  const amount = Number(state.value?.qualifyingAmount ?? 0);
  let previous = 0;
  return (state.value?.thresholds ?? []).reduce((total, threshold) => {
    const current = Number(threshold);
    const fraction = Math.max(0, Math.min(1, (amount - previous) / (current - previous)));
    previous = current;
    return total + fraction * 25;
  }, 0);
});

function money(amount: string | number) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: state.value?.currency ?? 'USD', maximumFractionDigits: 2 })
    .format(Number(amount));
}

function rememberOpener() {
  opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
}

async function restoreFocus() {
  await nextTick();
  if (opener?.isConnected && !opener.matches(':disabled')) opener.focus();
  else heading.value?.focus();
}

function openPicker() {
  rememberOpener();
  errorMessage.value = '';
  showPicker.value = true;
}

function closePicker() {
  showPicker.value = false;
}

function showAccount() {
  rememberOpener();
  showLogin.value = true;
}

function openSelection() {
  if (!state.value?.enabled || state.value.unavailableReason || !state.value.availableTier || busy.value) return;
  if (authStore.isAuthenticated) openPicker();
  else showAccount();
}

defineExpose({ openSelection });

function closeAccount() {
  showLogin.value = false;
  restoreFocus();
}

watch(showPicker, async (visible) => {
  if (lockedDialog) {
    enableBodyScroll(lockedDialog);
    lockedDialog = null;
  }
  if (visible) {
    await nextTick();
    if (!showPicker.value || !pickerDialog.value) return;
    lockedDialog = pickerDialog.value;
    disableBodyScroll(lockedDialog);
    closeButton.value?.focus();
  } else {
    restoreFocus();
  }
});

watch(() => state.value, (current, previous) => {
  if (previous?.selectedProductId && !current?.selectedProductId && !isMutating.value) {
    selectionNotice.value = 'Your free library was removed because it is no longer eligible for this basket. Choose again from your current tier.';
  } else if (current?.selectedProductId) {
    selectionNotice.value = '';
  }
  if (!current?.enabled || current.unavailableReason || !current.availableTier || !authStore.isAuthenticated) closePicker();
});

watch(() => authStore.isAuthenticated, (authenticated) => {
  if (!authenticated) closePicker();
});

function trapFocus(event: KeyboardEvent) {
  if (event.key !== 'Tab' || !pickerDialog.value) return;
  const focusable = Array.from(pickerDialog.value.querySelectorAll<HTMLElement>(
    'button:not([disabled]), a[href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
  ));
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (!first || !last) {
    event.preventDefault();
    pickerDialog.value.focus();
  } else if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

async function select(productId: number) {
  if (busy.value) return;
  errorMessage.value = '';
  isMutating.value = true;
  try {
    await cartStore.selectFreePick(productId);
    selectionNotice.value = '';
    closePicker();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Could not select this free library. Refresh your basket and try again.';
  } finally {
    isMutating.value = false;
  }
}

async function clearSelection() {
  if (busy.value) return;
  errorMessage.value = '';
  isMutating.value = true;
  try {
    await cartStore.clearFreePick();
    selectionNotice.value = '';
    await restoreFocus();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Could not remove the free library. Refresh your basket and try again.';
  } finally {
    isMutating.value = false;
  }
}

async function loadMoreCandidates() {
  if (isLoadingCandidates.value) return;
  isLoadingCandidates.value = true;
  errorMessage.value = '';
  try {
    await cartStore.loadMoreFreePickCandidates();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Could not load more free libraries. Please try again.';
  } finally {
    isLoadingCandidates.value = false;
  }
}

async function afterLogin() {
  showLogin.value = false;
  errorMessage.value = '';
  try {
    await cartStore.refreshCart();
    if (state.value?.enabled && !state.value.unavailableReason && state.value.availableTier > 0) showPicker.value = true;
    else restoreFocus();
  } catch (error) {
    errorMessage.value = error instanceof Error ? error.message : 'Your basket could not be refreshed after sign in. Please refresh the page.';
    restoreFocus();
  }
}

onBeforeUnmount(() => {
  if (lockedDialog) enableBodyScroll(lockedDialog);
});
</script>

<style lang="scss" scoped>
.free-pick {
  background: #f2f2f3;
  color: #1c1c1f;
  margin-bottom: 1.5rem;
  padding: 1.5rem;
  text-align: left;
  min-width: 0;
  &__title { font-size: 2.1rem; font-weight: 500; line-height: 1.1; margin: 0 0 1.5rem; }
  &__progress { height: 6px; background: #cdcdd2; border-radius: 3px; position: relative; margin: .5rem 0 2.75rem; }
  &__progress i { position: absolute; inset: 0 auto 0 0; background: #4f9e3f; border-radius: 3px; }
  &__marker { position: absolute; top: 1rem; transform: translateX(-100%); font-size: .75rem; white-space: nowrap; color: #66666c; }
  &__marker::before { content: ''; position: absolute; top: -1.3rem; right: 0; width: 16px; height: 16px; border: 3px solid #f2f2f3; border-radius: 50%; background: #cdcdd2; }
  &__marker.is-unlocked { color: #1c1c1f; }
  &__marker.is-unlocked::before { background: #4f9e3f; }
  &__status { font-size: 1.05rem; margin: 0 0 .5rem; }
  &__status span { display: block; }
  &__hint { color: #66666c; font-size: .8rem; margin: 0 0 .75rem; }
  &__slots { list-style: none; padding: 0; margin: 1.25rem 0 0; max-width: 320px; }
  &__slot { background: #fff; padding: .65rem; border: 2px solid transparent; min-width: 0; }
  &__slot.is-active { border-color: #1c1c1f; }
  &__tier-name { font-size: .75rem; text-transform: uppercase; letter-spacing: .04em; font-weight: 600; margin: 0; }
  &__tier-value { font-size: .75rem; color: #66666c; margin: .25rem 0 .75rem; overflow-wrap: anywhere; }
  &__guest { margin: 0; font-size: .875rem; }
  &__button { background: #e2820f; color: #1c1c1f; padding: .6rem .5rem; font-size: .875rem; font-weight: 700; width: 100%; }
  &__button--dark { background: #1c1c1f; color: #fff; }
  &__text-button, &__links button { background: transparent; padding: 0; font-size: .8rem; text-decoration: underline; text-align: left; }
  &__links { display: flex; flex-wrap: wrap; gap: .75rem; }
  &__note { background: #fff4dc; padding: .75rem; margin: .75rem 0; font-size: .875rem; }
  &__error { color: #a40000; margin: .75rem 0 0; }
  &--compact { padding: .75rem; }
  &--compact &__title { font-size: 1.4rem; }
  button:disabled, :deep(button:disabled) { opacity: .6; cursor: wait; }
  button:focus-visible { outline: 2px solid #1c1c1f; outline-offset: 3px; }
  @media (max-width: 720px) {
    padding: 1rem;
    &__title { font-size: 1.75rem; }
  }
}
.free-pick__overlay {
  position: fixed; inset: 0; z-index: 1100; background: rgb(20 20 24 / 55%); display: flex; align-items: center; justify-content: center; padding: 1rem;
}
.free-pick__dialog {
  background: #fff; color: #1c1c1f; width: min(760px, 100%); max-height: 86dvh; overflow-y: auto; overscroll-behavior: contain; padding: 1.25rem;
  button:focus-visible { outline: 2px solid #1c1c1f; outline-offset: 3px; }
  button:disabled { opacity: .6; cursor: default; }
}
.free-pick__dialog-header { display: flex; justify-content: space-between; align-items: baseline; gap: 1rem; margin-bottom: .75rem; }
.free-pick__dialog-header h3 { font-size: 1.1rem; font-weight: 700; margin: 0; }
.free-pick__close { font-size: 1.5rem; padding: .25rem; }
.free-pick__grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: 1rem; }
.free-pick__load { margin-top: 1rem; }
@media (max-width: 380px) {
  .free-pick__dialog { padding: .75rem; }
  .free-pick__grid { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .75rem; }
}
</style>
